import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Stack, Card, CardContent,
  useMediaQuery, useTheme, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { getAdminVerifications } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import useListQueryState from "../../../hooks/useListQueryState";

const STATUS_TABS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const verificationStatusColor = { PENDING: "info", APPROVED: "success", REJECTED: "error" };

const EMPTY_MESSAGE = {
  PENDING: "No pending verifications.",
  APPROVED: "No approved verifications yet.",
  REJECTED: "No rejected verifications.",
};

const DATE_LABEL = { PENDING: "Submitted", APPROVED: "Reviewed", REJECTED: "Reviewed" };

const VerificationList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const status = searchParams.get("status") || "PENDING";
  const page = Number(searchParams.get("page")) || 0;
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-verifications", status, page],
    queryFn: () => getAdminVerifications({ page: page + 1, limit, status }),
  });

  const handleRowClick = (userId) =>
    navigate(generatePath(frontendRoutes.ADMIN_VERIFICATION_DETAIL, { userId }));

  const handleStatusChange = (_, value) => {
    if (!value) return;
    updateParams({ status: value, page: undefined });
  };

  const users = data?.users || [];
  const dateField = status === "PENDING" ? "createdAt" : "updatedAt";

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Verifications</Typography>

      <ToggleButtonGroup
        value={status}
        exclusive
        onChange={handleStatusChange}
        size="small"
        sx={{ mb: 2 }}
      >
        {STATUS_TABS.map((tab) => (
          <ToggleButton key={tab.value} value={tab.value}>{tab.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {isLoading ? (
        <CircularProgress />
      ) : users.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{EMPTY_MESSAGE[status]}</Typography>
      ) : isMobile ? (
        <Stack spacing={2}>
          {users.map((u) => (
            <Card key={u._id} variant="outlined" onClick={() => handleRowClick(u._id)} sx={{ cursor: "pointer" }}>
              <CardContent>
                <Typography fontWeight={600}>{u.name}</Typography>
                <Typography variant="body2">{u.email || u.phoneNumber || "—"}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Chip label={u.verifiedIdType || "—"} size="small" />
                  <Chip label={u.verificationStatus} color={verificationStatusColor[u.verificationStatus]} size="small" />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email / Phone</TableCell>
              <TableCell>ID Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>{DATE_LABEL[status]}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u._id} hover onClick={() => handleRowClick(u._id)} sx={{ cursor: "pointer" }}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email || u.phoneNumber || "—"}</TableCell>
                <TableCell><Chip label={u.verifiedIdType || "—"} size="small" /></TableCell>
                <TableCell>
                  <Chip label={u.verificationStatus} color={verificationStatusColor[u.verificationStatus]} size="small" />
                </TableCell>
                <TableCell>{u[dateField] ? new Date(u[dateField]).toLocaleDateString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default VerificationList;
